import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import {
  CollectionActivityCursor,
  CollectionActivityEntry,
  ListCollectionActivityRequest,
  ListCollectionActivityResult,
} from '@qkindex/contracts';
import {
  ListIcon,
  DeleteIcon,
  EditIcon,
  InfoIcon,
  InventoryIcon,
  LocationIcon,
  LogoutIcon,
  ShareIcon,
  UserPlusIcon,
  UsersIcon,
} from '../../../components/ui/icons';
import { functions } from '../../../firebase';
import { LoadingState, SectionEmptyState } from '../../../components/ui/States';
import { userProfileResolverService } from '../../../services/userProfileResolverService';

const listActivity = httpsCallable<
  ListCollectionActivityRequest,
  ListCollectionActivityResult
>(functions, 'listCollectionActivity');

function activityIcon(action: string) {
  if (action.startsWith('Invitation')) return <UserPlusIcon />;
  if (action === 'Member left') return <LogoutIcon />;
  if (action.startsWith('Member')) return <UsersIcon />;
  if (action === 'Collection renamed') return <EditIcon />;
  if (action === 'Collection GPS updated') return <LocationIcon />;
  if (action === 'Tags or sharing updated') return <ShareIcon />;
  if (action === 'Items permanently deleted') return <DeleteIcon />;
  if (action.startsWith('Item')) return <InventoryIcon />;
  return <InfoIcon />;
}

function activityDay(date: Date, today: Date) {
  if (date.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function CollectionActivitySection({
  collectionId,
  userId,
  membershipVersion,
}: {
  collectionId: string;
  userId?: string;
  membershipVersion?: number;
}) {
  const [actorNames, setActorNames] = useState<Record<string, string>>({});
  const [entries, setEntries] = useState<CollectionActivityEntry[]>([]);
  const [cursor, setCursor] = useState<CollectionActivityCursor>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const contextKey = JSON.stringify([collectionId, userId, membershipVersion]);
  const activeContext = useRef(contextKey);
  activeContext.current = contextKey;
  const epoch = useRef(0);
  const load = useCallback(
    async (nextCursor?: CollectionActivityCursor) => {
      const request = ++epoch.current;
      setLoading(true);
      setError(false);
      try {
        const { data } = await listActivity({
          collectionId,
          ...(nextCursor ? { cursor: nextCursor } : {}),
        });
        if (request !== epoch.current || contextKey !== activeContext.current)
          return;
        setEntries((previous) =>
          nextCursor
            ? [
                ...previous,
                ...data.entries.filter(
                  (entry) =>
                    !previous.some((old) => old.eventId === entry.eventId),
                ),
              ]
            : data.entries,
        );
        setCursor(data.nextCursor);
      } catch {
        if (request !== epoch.current || contextKey !== activeContext.current)
          return;
        // A permission failure must not leave a former administrator's activity visible.
        setEntries([]);
        setCursor(undefined);
        setError(true);
      } finally {
        if (request === epoch.current && contextKey === activeContext.current)
          setLoading(false);
      }
    },
    [collectionId, contextKey],
  );
  useEffect(() => {
    setEntries([]);
    setCursor(undefined);
    void load();
    return () => {
      epoch.current += 1;
    };
  }, [load]);
  useEffect(() => {
    let active = true;
    setActorNames({});
    if (membershipVersion !== undefined) {
      const ids = [
        ...new Set(entries.map((entry) => entry.actorUserId)),
      ].filter((id) => id !== userId && id !== 'system');
      void Promise.all(
        ids.map(
          async (id) =>
            [
              id,
              await userProfileResolverService.resolveUsername(
                collectionId,
                id,
                membershipVersion,
              ),
            ] as const,
        ),
      ).then((names) => {
        if (active)
          setActorNames(
            Object.fromEntries(
              names.flatMap(([id, name]) => (name ? [[id, name]] : [])),
            ),
          );
      });
    }
    return () => {
      active = false;
    };
  }, [entries, collectionId, membershipVersion, userId]);
  const today = new Date();
  const groups = new Map<
    string,
    { date: Date; entries: CollectionActivityEntry[] }
  >();
  entries.forEach((entry) => {
    const date = new Date(entry.createdAt.seconds * 1000);
    const key = date.toDateString();
    const group = groups.get(key);
    if (group) group.entries.push(entry);
    else groups.set(key, { date, entries: [entry] });
  });
  return (
    <Stack spacing={1}>
      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              disabled={loading}
              onClick={() => {
                void load();
              }}
            >
              Try again
            </Button>
          }
        >
          Activity could not be loaded. Check your access and try again.
        </Alert>
      )}
      {loading && entries.length === 0 && (
        <LoadingState label="Loading activity…" />
      )}
      {!loading && !error && entries.length === 0 && (
        <SectionEmptyState
          icon={ListIcon}
          message={cursor ? 'No activity on this page.' : 'No activity yet.'}
        />
      )}
      {Array.from(groups, ([key, group], index) => (
        <Box
          component="section"
          key={key}
          aria-label={activityDay(group.date, today)}
          sx={{
            borderTop: index ? 1 : 0,
            borderColor: 'divider',
            pt: index ? 2 : 0,
          }}
        >
          <Typography
            component="h3"
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, px: 1, mb: 0.5 }}
          >
            {activityDay(group.date, today)}
          </Typography>
          <List
            disablePadding
            aria-label={`${activityDay(group.date, today)} activity`}
          >
            {group.entries.map((entry) => {
              const date = new Date(entry.createdAt.seconds * 1000);
              const actor =
                entry.actorUserId === userId
                  ? 'You'
                  : entry.actorUserId === 'system'
                    ? 'System'
                    : actorNames[entry.actorUserId]
                      ? `@${actorNames[entry.actorUserId]}`
                      : 'Member';
              return (
                <ListItem key={entry.eventId} sx={{ px: 1, py: 1.5, gap: 2 }}>
                  <ListItemIcon sx={{ minWidth: 0, color: 'primary.main' }}>
                    {activityIcon(entry.action)}
                  </ListItemIcon>
                  <ListItemText
                    sx={{ m: 0, minWidth: 0 }}
                    primary={entry.action}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 600,
                      sx: { overflowWrap: 'anywhere' },
                    }}
                    secondary={
                      <>
                        <bdi dir="auto">{actor}</bdi> ·{' '}
                        <time dateTime={date.toISOString()}>
                          {date.toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </time>
                      </>
                    }
                    secondaryTypographyProps={{
                      variant: 'body2',
                      mt: 0.5,
                      sx: { overflowWrap: 'anywhere' },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
      {loading && entries.length > 0 && (
        <LoadingState label="Loading more activity…" />
      )}
      {cursor && !loading && (
        <Button
          onClick={() => {
            void load(cursor);
          }}
        >
          Load more activity
        </Button>
      )}
    </Stack>
  );
}
